import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
} from '@mui/material';
import axiosInstance from '../api/axiosConfig';
import { useSnackbar } from './common/SnackbarProvider';

interface AddFamilyMemberDialogProps {
  open: boolean;
  /** The existing PRIMARY member the new person is linked to. */
  primaryId: string;
  primaryName?: string;
  onClose: () => void;
  /** Called after a successful add so the parent can refetch. */
  onAdded: () => void;
}

// Spouse/Partner become a SECONDARY member (their own login); everyone else is a
// DEPENDENT (no login needed). Mirrors the household form + the API hierarchy.
const RELATIONSHIPS: Array<{ value: string; label: string; secondary: boolean }> = [
  { value: 'CHILD', label: 'Child', secondary: false },
  { value: 'SPOUSE', label: 'Spouse', secondary: true },
  { value: 'PARTNER', label: 'Partner', secondary: true },
  { value: 'PARENT', label: 'Parent', secondary: false },
  { value: 'SIBLING', label: 'Sibling', secondary: false },
  { value: 'OTHER', label: 'Other', secondary: false },
];

const emptyForm = {
  first_name: '',
  last_name: '',
  date_of_birth: '',
  email: '',
  password: '',
  // Emergency contact (optional at create — admin can also add later via edit).
  emergency_contact_name: '',
  emergency_contact_phone: '',
  emergency_contact_relationship: '',
};

const AddFamilyMemberDialog: React.FC<AddFamilyMemberDialogProps> = ({
  open,
  primaryId,
  primaryName,
  onClose,
  onAdded,
}) => {
  const snackbar = useSnackbar();
  const [relationship, setRelationship] = useState('CHILD');
  const [form, setForm] = useState({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSecondary = RELATIONSHIPS.find((r) => r.value === relationship)?.secondary ?? false;

  const reset = () => {
    setRelationship('CHILD');
    setForm({ ...emptyForm });
    setError(null);
  };
  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First and last name are required.');
      return;
    }
    if (isSecondary && (!form.email.trim() || !form.password)) {
      setError('A spouse/partner gets their own login, so they need an email and password.');
      return;
    }
    setError(null);
    setSubmitting(true);

    const payload: any = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      date_of_birth: form.date_of_birth || undefined,
      member_type: isSecondary ? 'SECONDARY' : 'DEPENDENT',
      primary_member_id: primaryId,
      relationship_to_primary: relationship,
      // Pass emergency contact through; backend stores them as nullable columns
      // on Member. Empty strings -> undefined so we don't overwrite with blanks.
      emergency_contact_name: form.emergency_contact_name.trim() || undefined,
      emergency_contact_phone: form.emergency_contact_phone.trim() || undefined,
      emergency_contact_relationship: form.emergency_contact_relationship.trim() || undefined,
    };
    if (isSecondary) {
      payload.email = form.email.trim();
      payload.password = form.password;
    }

    try {
      await axiosInstance.post('/members/admin-create', payload);
      snackbar.success('Family member added.');
      reset();
      onAdded();
      onClose();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Failed to add family member'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Family Member{primaryName ? ` — ${primaryName}` : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            Adds a person to this member's household, linked to them.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Relationship</InputLabel>
            <Select
              label="Relationship"
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
            >
              {RELATIONSHIPS.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              required
              label="First Name"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            />
            <TextField
              fullWidth
              required
              label="Last Name"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            />
          </Box>
          <TextField
            fullWidth
            type="date"
            label="Date of Birth"
            InputLabelProps={{ shrink: true }}
            value={form.date_of_birth}
            onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
          />
          {isSecondary && (
            <>
              <Typography variant="caption" color="text.secondary">
                A spouse/partner gets their own login.
              </Typography>
              <TextField
                fullWidth
                required
                type="email"
                label="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <TextField
                fullWidth
                required
                type="password"
                label="Password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </>
          )}

          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">Emergency contact (optional)</Typography>
          </Divider>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              label="Contact name"
              value={form.emergency_contact_name}
              onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Relationship"
              placeholder="e.g. Mom, Dad, Aunt"
              value={form.emergency_contact_relationship}
              onChange={(e) => setForm({ ...form, emergency_contact_relationship: e.target.value })}
            />
          </Box>
          <TextField
            fullWidth
            label="Contact phone"
            placeholder="(555) 123-4567"
            value={form.emergency_contact_phone}
            onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? 'Adding…' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddFamilyMemberDialog;
